import { breakpoint } from 'utility/breakpoints'
import gsap from 'gsap'

const loader = {
  el: {
    header: document.querySelector<HTMLElement>('.header'),
    links: {
      all: document.querySelectorAll<HTMLElement>(`
        .header__logo,
        .header__navigation-link,
        .header__book-time-button,
        .mobile-header__logo,
        .mobile-header__menu-link,
        .featured-block__button,
        .featured-products-block__button,
        .footer__navigation-link,
        .footer__contact-button
      `),
      cartTriggers: document.querySelectorAll<HTMLElement>('.header__cart-trigger, .mobile-header__cart-trigger'),
      footer: document.querySelectorAll<HTMLElement>('.footer__navigation-link'),
      header: document.querySelectorAll<HTMLElement>('.header__navigation-link'),
      mobile: document.querySelectorAll<HTMLElement>('.mobile-header__menu-link'),
    },
    loader: document.querySelector<HTMLElement>('.loader'),
    productsBannerWhiteBackground: document.querySelector<HTMLElement>('.products-banner__white-background'),
  },

  init(): void {
    if (sessionStorage.getItem('pageLoaded') !== 'true') {
      this.initialPageLoad()
    }

    if (sessionStorage.getItem('pageTransition') === 'true') {
      this.renderSquares(this.calculateAmount())
      this.setPosition()
      this.animateOnPageEnter()
    }

    this.el.links.all.forEach((link) => {
      link.addEventListener('click', (event) => {
        this.redirect(event, link)
      })
    })

    this.el.links.cartTriggers.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (Number.parseFloat(link.dataset.count) < 1) {
          this.redirect(event, link)
        }
      })
    })
  },

  animateOnPageEnter(): void {
    this.handleProductsBanner('onPageEnter')

    gsap.fromTo(
      this.el.loader.children,
      {
        opacity: 1,
      },
      {
        duration: 0.3,
        ease: 'none',
        onComplete: () => {
          sessionStorage.setItem('pageTransition', 'false')
        },
        opacity: 0,
        stagger: 0.006,
      }
    )
  },

  animateOnPageLeave(link): void {
    gsap
      .timeline()
      .set(document.body, { pointerEvents: 'none' })
      .to(this.el.loader.children, {
        duration: 0.2,
        ease: 'none',
        onComplete: () => {
          sessionStorage.setItem('pageTransition', 'true')
          window.location.href = link.getAttribute('href')
        },
        onStart: () => {
          this.handleActiveLink(link)
          this.handleProductsBanner('onPageLeave')
        },
        opacity: 1,
        stagger: 0.006,
      })
  },

  calculateAmount(): number {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const columnCount =
      viewportWidth < breakpoint.tablet ? 4 : viewportWidth < breakpoint.desktop.s ? 6 : viewportWidth < breakpoint.desktop.l ? 8 : 12
    const squareWidth = viewportWidth / columnCount
    const squaresHorizontal = Math.floor(viewportWidth / squareWidth)
    const squaresVertical = Math.floor(viewportHeight / squareWidth)
    const totalSquares = squaresHorizontal * squaresVertical

    return totalSquares + columnCount
  },

  handleActiveLink(link): void {
    this.el.links.footer.forEach((link) => link.classList.remove('footer__navigation-link--active'))
    this.el.links.header.forEach((link) => link.classList.remove('header__navigation-link--active'))
    this.el.links.mobile.forEach((link) => link.classList.remove('mobile-header__menu-link--active'))

    link.classList.contains('footer__navigation-link') && link.classList.add('footer__navigation-link--active')
    link.classList.contains('header__navigation-link') && link.classList.add('header__navigation-link--active')
    link.classList.contains('mobile-header__menu-link') && link.classList.add('mobile-header__menu-link--active')
  },

  handleProductsBanner(action): void {
    if (!this.el.productsBannerWhiteBackground) {
      return
    }

    switch (action) {
      case 'onPageLeave':
        gsap.to(this.el.productsBannerWhiteBackground, {
          duration: 0.321,
          ease: 'none',
          opacity: 1,
        })

        this.el.header.classList.remove('header--black-friday')
        break
      case 'onPageEnter':
        gsap
          .timeline()
          .set(this.el.productsBannerWhiteBackground, {
            opacity: 1,
          })
          .to(
            this.el.productsBannerWhiteBackground,
            {
              clearProps: 'all',
              duration: 1.234,
              ease: 'power3.out',
              opacity: 0,
            },
            0.1
          )
        break
    }
  },

  initialPageLoad(): void {
    document.body.classList.remove('preload')

    gsap
      .timeline()
      .set(this.el.loader, {
        background: '#fff',
        zIndex: 999,
      })
      .to(this.el.loader, {
        clearProps: 'all',
        delay: 0.234,
        duration: 1.234,
        ease: 'power3.out',
        opacity: 0,
      })
      .call(
        () => {
          sessionStorage.setItem('pageLoaded', 'true')
        },
        null,
        '<0.5'
      )
  },

  redirect(event, link): void {
    if (link.getAttribute('href') !== window.location.pathname) {
      event.preventDefault()
      this.renderSquares(this.calculateAmount())
      this.setPosition()
      this.animateOnPageLeave(link)
    }
  },

  renderSquares(amount): void {
    this.el.loader.innerHTML = ''

    Array.from({ length: amount }).forEach(() => {
      this.el.loader.insertAdjacentHTML('beforeend', '<div></div>')
    })
  },

  setPosition(): void {
    const top = this.el.header.getBoundingClientRect().bottom

    gsap.set(this.el.loader, {
      top: top > 0 && window.innerWidth >= breakpoint.tablet ? top : 0,
    })
  },
}

export default loader
